/**
 * Gradient skybox for the 3D scene
 * OPTIMIZED: Using 32-segment sphere (good balance between quality and performance)
 */

import type * as THREE_NS from 'three';
import {
  gradientSphereVertexShader,
  createGradientSphereFragmentShader,
} from '../utils/shaders/gradientSphereShader.js';
import type { SkyboxGradientConfig } from './jsPlacementTowerConfig.js';

export function createGradientSphere(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS,
  scale: number = 1.5,
  gradient: SkyboxGradientConfig
): THREE_NS.Mesh {
  const { SphereGeometry, ShaderMaterial, Mesh, BackSide, Vector3 } = THREE as any;

  const baseRadius = 85;
  // OPTIMIZATION: Using 32-segment sphere for smooth gradient without corners
  // 32x32 segments = 1024 faces, with BackSide = 1024 faces (only inside rendered)
  // This provides smooth gradient without the obvious corners of a cube
  const segments = 32;
  const gradientSphereGeometry = new SphereGeometry(baseRadius * scale, segments, segments);

  // Create fragment shader with gradient config
  const fragmentShader = createGradientSphereFragmentShader();

  const material = new ShaderMaterial({
    vertexShader: gradientSphereVertexShader,
    fragmentShader: fragmentShader,
    side: BackSide, // OPTIMIZATION: Only render inside faces (skybox), not outside - 50% fewer faces
    uniforms: {
      uBlueHue: { value: gradient.blueHue },
      uBlueSaturation: { value: gradient.blueSaturation },
      uBlueLightness: { value: gradient.blueLightness },
      uWhite: { value: new Vector3(gradient.whiteR, gradient.whiteG, gradient.whiteB) },
      uBlack: { value: new Vector3(gradient.blackR, gradient.blackG, gradient.blackB) },
      uThreshold1: { value: gradient.threshold1 },
      uThreshold2: { value: gradient.threshold2 },
      uThreshold3: { value: gradient.threshold3 },
    },
  });

  const sphere = new Mesh(gradientSphereGeometry, material);
  sphere.position.set(0, 0, 0);
  scene.add(sphere);

  return sphere;
}
